import { Link } from "wouter";
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  Compass,
  Eye,
  FlaskConical,
  Layers,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import { Button } from "@/components/ui/button";

interface FeatureItem {
  icon: typeof Sparkles;
  title: string;
  body: string;
}

const WHAT_IT_DOES: FeatureItem[] = [
  {
    icon: Layers,
    title: "Rank protein interaction candidates",
    body: "Paste a gene or protein list and get back interaction candidates ranked by a 6-factor lab-priority model — not raw network dumps.",
  },
  {
    icon: Eye,
    title: "Surface evidence and caveats up front",
    body: "Every candidate carries its evidence tier, channel mix, conservation, disease relevance, and what's missing — never collapsed into one opaque score.",
  },
  {
    icon: FlaskConical,
    title: "Help you decide what to test first",
    body: "Each interaction comes with assay recommendations and Validate / Maybe later / Reject decisions, so the output of an analysis is a lab-ready shortlist.",
  },
];

const WHY_TRUST: FeatureItem[] = [
  {
    icon: ShieldCheck,
    title: "Transparent provenance",
    body: "Scores break down by source (STRING, UniProt, Open Targets, AlphaFold) and by STRING channel. The drawer shows which channels drove the rank and how many corroborate.",
  },
  {
    icon: AlertCircle,
    title: "Explicit caveats",
    body: "Text mining and AlphaFold support are never sold as binding proof. Persistent banners and per-row uncertainty badges keep the limits of every signal visible.",
  },
  {
    icon: Compass,
    title: "Visible biological context",
    body: "Tissue, cell type, repair state, and disease mode are first-class controls. Active context is shown as a banner — never silently applied in the background.",
  },
  {
    icon: BookOpen,
    title: "Hypotheses, not validation",
    body: "ACADEM prioritises what to test next. It does not prove an interaction in vivo — wet-lab confirmation is still required, and the UI says so on every surface.",
  },
];

const HOW_TO_START: string[] = [
  "Enter one or more genes, protein names, or UniProt accessions.",
  "Choose a species (8 model organisms supported).",
  "Optionally apply a Pathology Preset or set tissue / cell type / disease mode.",
  "Run the search, then open any row to inspect the evidence and decide.",
];

const WHATS_NEXT: FeatureItem[] = [
  {
    icon: Layers,
    title: "Compare candidates",
    body: "Use Compare genes to see Shared, Unique, Hubs, and Bridges across a multi-gene panel.",
  },
  {
    icon: ClipboardList,
    title: "Log decisions",
    body: "Mark each interaction Validate now, Maybe later, or Reject — with an optional reason. Decisions persist across sessions.",
  },
  {
    icon: BookOpen,
    title: "Save panels",
    body: "Re-run a query later from Saved Panels with the same filters and context attached.",
  },
  {
    icon: ArrowRight,
    title: "Export for downstream use",
    body: "Export the full analysis or your decisions as CSV or JSON, with version metadata baked into the header.",
  },
];

function SectionHeader({ eyebrow, title, body }: { eyebrow: string; title: string; body?: string }) {
  return (
    <div className="space-y-2">
      <div className="label-eyebrow">{eyebrow}</div>
      <h2 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">{title}</h2>
      {body && <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{body}</p>}
    </div>
  );
}

function FeatureCard({ item }: { item: FeatureItem }) {
  const Icon = item.icon;
  return (
    <div className="flex gap-3 rounded-lg border border-border/80 bg-card p-4 shadow-sm">
      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-md bg-accent/10 text-accent">
        <Icon className="h-4 w-4" />
      </span>
      <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground">{item.title}</div>
        <p className="text-xs leading-relaxed text-muted-foreground">{item.body}</p>
      </div>
    </div>
  );
}

export function WelcomePage() {
  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:py-14">
      <section className="flex flex-col items-center text-center">
        <span
          className="grid h-14 w-14 place-items-center rounded-full text-white shadow-[0_10px_36px_-10px_hsl(186_84%_40%/0.65)]"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, hsl(186 84% 50%), hsl(213 60% 22%))",
          }}
          aria-hidden
        >
          <Sparkles className="h-6 w-6" strokeWidth={2} />
        </span>
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-0.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Welcome
        </div>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          ACA<span className="text-accent">DEM</span>
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Context-aware protein interaction analysis for lab-ready prioritization. Turn a gene or
          protein list into ranked, evidence-tiered interaction candidates &mdash; with caveats and
          biological context surfaced, not buried.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/">
            <Button
              size="lg"
              className="h-11 gap-2 bg-accent px-5 text-accent-foreground hover:bg-accent/90"
            >
              <Search className="h-4 w-4" />
              Start a query
            </Button>
          </Link>
          <Link to="/guide">
            <Button size="lg" variant="outline" className="h-11 gap-2 px-5">
              <BookOpen className="h-4 w-4" />
              See how it works
            </Button>
          </Link>
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground">
          About 30 seconds to read &middot; you can revisit this page from the header any time.
        </div>
      </section>

      <section className="mt-12 space-y-4">
        <SectionHeader
          eyebrow="What ACADEM does"
          title="From a gene list to a ranked experimental agenda"
          body="ACADEM integrates STRING, UniProt, Open Targets, and AlphaFold with a curated biological-context knowledge base — and applies a 6-factor priority model on top."
        />
        <div className="grid gap-3 sm:grid-cols-3">
          {WHAT_IT_DOES.map((item) => (
            <FeatureCard key={item.title} item={item} />
          ))}
        </div>
      </section>

      <section className="mt-12 space-y-4">
        <SectionHeader
          eyebrow="Why you can trust the output"
          title="Built for scientific judgement, not blind ranking"
          body="The product principles are non-negotiable: evidence provenance is always visible, gaps are surfaced as first-class information, and every score can be inspected."
        />
        <div className="grid gap-3 sm:grid-cols-2">
          {WHY_TRUST.map((item) => (
            <FeatureCard key={item.title} item={item} />
          ))}
        </div>
      </section>

      <section className="mt-12 grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="space-y-4">
          <SectionHeader eyebrow="How to start" title="Four steps to your first analysis" />
          <ol className="space-y-2.5">
            {HOW_TO_START.map((step, i) => (
              <li
                key={step}
                className="flex items-start gap-3 rounded-md border border-border/80 bg-card px-3 py-2.5 shadow-sm"
              >
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent/10 text-[11px] font-semibold tabular-nums text-accent">
                  {i + 1}
                </span>
                <span className="text-sm leading-relaxed text-foreground">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-4">
          <SectionHeader eyebrow="What happens next" title="A workspace, not a one-off search" />
          <div className="grid gap-3">
            {WHATS_NEXT.map((item) => (
              <FeatureCard key={item.title} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="mt-12 rounded-lg border border-[hsl(38_90%_60%/0.5)] bg-[hsl(44_94%_95%)] px-4 py-3 text-xs leading-relaxed text-[hsl(28_80%_28%)] dark:bg-[hsl(38_42%_16%)] dark:text-[hsl(44_90%_82%)]">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <strong>Scientific scope.</strong> ACADEM is a decision-support tool. It prioritises
            interaction hypotheses but does not confirm them <em>in vivo</em>. AlphaFold support is
            structural plausibility, not binding evidence. Open Targets disease scores are aggregated
            relevance, not mechanistic proof. Apply domain knowledge before committing lab resources.
          </p>
        </div>
      </section>

      <section className="mt-10 flex flex-col items-center gap-4 rounded-xl border border-border/80 bg-muted/30 px-6 py-8 text-center">
        <CheckCircle2 className="h-7 w-7 text-accent" />
        <h3 className="font-display text-lg font-semibold">Ready when you are</h3>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          Run your first analysis with a single example query, or read the 5-step guide first &mdash;
          either path takes only a few minutes.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/">
            <Button className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
              <Search className="h-4 w-4" />
              Start a query
            </Button>
          </Link>
          <Link to="/guide">
            <Button variant="outline" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Read the quick guide
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
